import React, { useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import "@/assets/css/phoneinput-dark.css";
import "@/assets/css/phoneinput-dark-global.css";
import { registerRestaurantApplication } from "@/services/apiService";

export default function RestaurantRegistrationForm() {
  // Inline dark mode override for react-phone-input-2
  React.useEffect(() => {
    const styleId = "phoneinput-dark-override";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = `
          html.dark .react-tel-input .form-control,
          body.dark .react-tel-input .form-control {
            background-color: #232323 !important;
            color: #f3f4f6 !important;
            border-color: #444 !important;
          }
          html.dark .react-tel-input .flag-dropdown,
          body.dark .react-tel-input .flag-dropdown {
            background-color: #232323 !important;
            border-color: #444 !important;
          }
          html.dark .react-tel-input .country-list,
          body.dark .react-tel-input .country-list {
            background-color: #232323 !important;
            color: #f3f4f6 !important;
            border-color: #444 !important;
          }
          html.dark .react-tel-input .country-list .country,
          body.dark .react-tel-input .country-list .country {
            color: #f3f4f6 !important;
          }
          html.dark .react-tel-input .selected-flag,
          body.dark .react-tel-input .selected-flag {
            background-color: #232323 !important;
            border-color: #444 !important;
          }
        `;
      document.head.appendChild(style);
    }
  }, []);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);
  const [formData, setFormData] = useState({
    city: "",
    country: "",
    email: "",
    full_name: "",
    password: "",
    phone_number: "",
    postal_code: "",
    province: "",
    restaurant_name: "",
    restaurant_type: "", // optional
    street_address: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<"error" | "success">("success");
  const [errorDetail, setErrorDetail] = useState<null | string>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (value: string) => {
    setFormData((prev) => ({ ...prev, phone_number: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        agreed_privacy: agreedPrivacy ? 1 : 0,
        agreed_terms: agreedTerms ? 1 : 0,
        city: formData.city || "N/A",
        country: formData.country || "N/A",
        email: formData.email,
        full_name: formData.full_name,
        password: formData.password,
        phone_number: formData.phone_number,
        postal_code: formData.postal_code || "0000",
        province: formData.province || "N/A",
        restaurant_name: formData.restaurant_name,
        restaurant_type: formData.restaurant_type || "",
        role: "RestaurantOwner",
        street_address: formData.street_address || "N/A",
      };
      const result = await registerRestaurantApplication(payload);
      if (result.success) {
        setModalType("success");
        setErrorDetail(null);
        setShowModal(true);
      } else {
        // Log full backend error for debugging
        console.error("Backend error:", result);
        setModalType("error");
        setErrorDetail(result.message || JSON.stringify(result.errors) || null);
        setShowModal(true);
      }
    } catch (error: any) {
      let friendly =
        "There was an error submitting your registration. Please check your information and try again.";
      if (error && error.response) {
        const status = error.response.status;
        const data = error.response.data;
        if (status === 422) {
          if (
            typeof data === "string" &&
            data.toLowerCase().includes("already exist")
          ) {
            friendly =
              "An account with this email or phone number already exists. Please use a different email or log in.";
          } else if (
            data &&
            typeof data === "object" &&
            data.message &&
            data.message.toLowerCase().includes("already exist")
          ) {
            friendly =
              "An account with this email or phone number already exists. Please use a different email or log in.";
          } else if (data && typeof data === "object" && data.errors) {
            const firstError = Object.values(data.errors)[0];
            if (Array.isArray(firstError)) {
              friendly = firstError[0];
            } else if (typeof firstError === "string") {
              friendly = firstError;
            }
          } else {
            friendly =
              "Some of your information is invalid. Please review and try again.";
          }
        }
      } else if (
        error &&
        typeof error === "object" &&
        "message" in error &&
        typeof (error as { message?: unknown }).message === "string"
      ) {
        friendly = (error as { message: string }).message;
      }
      setModalType("error");
      setErrorDetail(friendly);
      setShowModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      className="mx-auto mt-4 w-full max-w-2xl rounded-2xl border border-base-200 bg-base-100 p-4 shadow-2xl dark:bg-base-200 sm:mt-8 sm:rounded-3xl sm:p-6 lg:p-10"
      onSubmit={handleSubmit}
    >
      <h2 className="mb-6 text-center text-2xl font-extrabold text-primary drop-shadow-lg sm:mb-8 sm:text-3xl lg:mb-10 lg:text-4xl">
        Register Your Restaurant
      </h2>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:mb-8 sm:gap-6 md:grid-cols-2 md:gap-8">
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-semibold sm:mb-2 sm:text-base">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            autoComplete="name"
            className="input input-sm input-bordered w-full sm:input-md"
            name="full_name"
            onChange={handleInputChange}
            placeholder="Your full name"
            required
            value={formData.full_name}
          />
        </div>
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-semibold sm:mb-2 sm:text-base">
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            autoComplete="email"
            className="input input-sm input-bordered w-full sm:input-md"
            name="email"
            onChange={handleInputChange}
            placeholder="you@email.com"
            required
            type="email"
            value={formData.email}
          />
        </div>
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-semibold sm:mb-2 sm:text-base">
            Password <span className="text-red-500">*</span>
          </label>
          <input
            autoComplete="new-password"
            className="input input-sm input-bordered w-full sm:input-md"
            name="password"
            onChange={handleInputChange}
            placeholder="Create a password"
            required
            type="password"
            value={formData.password}
          />
        </div>
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-semibold sm:mb-2 sm:text-base">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <PhoneInput
            buttonClass="phoneinput-dark-btn dark:!bg-[#232323] dark:!text-[#f3f4f6]"
            containerClass="w-full"
            country={"za"}
            data-theme={
              document.documentElement.classList.contains("dark")
                ? "dark"
                : "light"
            }
            dropdownClass="phoneinput-dark-dropdown dark:!bg-[#232323] dark:!text-[#f3f4f6]"
            inputClass="input input-bordered input-sm sm:input-md w-full phoneinput-dark dark:!bg-[#232323] dark:!text-[#f3f4f6] dark:!border-[#444]"
            onChange={handlePhoneChange}
            value={formData.phone_number}
          />
        </div>
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-semibold sm:mb-2 sm:text-base">
            Restaurant Name <span className="text-red-500">*</span>
          </label>
          <input
            className="input input-sm input-bordered w-full sm:input-md"
            name="restaurant_name"
            onChange={handleInputChange}
            placeholder="Restaurant name"
            required
            value={formData.restaurant_name}
          />
        </div>
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-semibold sm:mb-2 sm:text-base">
            Restaurant Type (optional)
          </label>
          <input
            className="input input-sm input-bordered w-full sm:input-md"
            name="restaurant_type"
            onChange={handleInputChange}
            placeholder="e.g. Fast Food, Cafe"
            value={formData.restaurant_type}
          />
        </div>
      </div>
      <div className="my-4 flex flex-col gap-2 sm:my-6">
        <label className="flex items-start gap-2 sm:items-center">
          <input
            checked={agreedTerms}
            className="checkbox checkbox-sm mt-0.5 sm:checkbox-md sm:mt-0"
            id="agreeTerms"
            onChange={(e) => setAgreedTerms(e.target.checked)}
            type="checkbox"
          />
          <span className="text-xs sm:text-sm">
            I agree to the Terms of Service
          </span>
        </label>
        <label className="flex items-start gap-2 sm:items-center">
          <input
            checked={agreedPrivacy}
            className="checkbox checkbox-sm mt-0.5 sm:checkbox-md sm:mt-0"
            id="agreePrivacy"
            onChange={(e) => setAgreedPrivacy(e.target.checked)}
            type="checkbox"
          />
          <span className="text-xs sm:text-sm">
            I agree to the Privacy Policy
          </span>
        </label>
      </div>
      <button
        className="btn btn-success btn-sm mt-2 w-full py-2 text-base font-bold tracking-wide sm:btn-md sm:py-3 sm:text-lg"
        disabled={
          isSubmitting ||
          !formData.full_name ||
          !formData.email ||
          !formData.password ||
          !formData.phone_number ||
          !formData.restaurant_name ||
          !agreedTerms ||
          !agreedPrivacy
        }
        type="submit"
      >
        {isSubmitting ? (
          <span className="loading loading-spinner loading-sm mr-2"></span>
        ) : null}
        {isSubmitting ? "Submitting..." : "Register Restaurant"}
      </button>
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 dark:bg-black/60">
          <div className="w-full max-w-md rounded-2xl bg-base-100 p-6 shadow-2xl dark:bg-base-200 sm:p-8 lg:p-10">
            <h3
              className={`mb-3 text-xl font-bold sm:mb-4 sm:text-2xl ${
                modalType === "success"
                  ? "text-green-600 dark:text-green-400"
                  : "text-red-600 dark:text-red-400"
              }`}
            >
              {modalType === "success"
                ? "Registration Successful"
                : "Submission Failed"}
            </h3>
            <p className="whitespace-pre-line text-sm text-base-content dark:text-base-content/80 sm:text-base">
              {modalType === "success"
                ? "Your restaurant registration has been submitted successfully. You can now log in and finish setting up your account and uploading your documents."
                : errorDetail ||
                  "There was an error submitting your registration. Please check your information and try again."}
            </p>
            <button
              className="btn btn-primary btn-sm mt-6 w-full rounded-full sm:btn-md sm:mt-8"
              onClick={() => {
                setShowModal(false);
                if (modalType === "success") window.location.href = "/";
              }}
            >
              Continue
            </button>
          </div>
        </div>
      )}
    </form>
  );
}
